import React, { useMemo } from 'react';
import type { Subscription, Invoice } from '../../../types/subscriptionManagement';

interface Props {
  subscriptions: Subscription[];
  invoices: Invoice[];
  /** When false, count subtitles refer to all data (not a single FY). */
  fyScoped?: boolean;
}

const KpiCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  color: string;
}> = ({ label, value, sub, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex">
    <div className={`w-1 flex-none self-stretch ${color}`} />
    <div className="flex-1 min-w-0 px-3 py-2.5">
      <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{label}</p>
      <p className="text-lg font-bold text-slate-800 leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

function fmtCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

export const SubscriptionKpiStrip: React.FC<Props> = ({
  subscriptions,
  invoices,
  fyScoped = true,
}) => {
  const kpis = useMemo(() => {
    const active = subscriptions.filter(s => s.status === 'Active');
    const totalMonthly = active.reduce((sum, s) => sum + (Number(s.monthlyCost) || 0), 0);
    const annualSubs = active.filter(s => s.billingCycle === 'Annual');
    const annualCommit = annualSubs.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    const unpaid = invoices.filter(i => i.paymentStatus === 'Unpaid' || i.paymentStatus === 'Overdue');
    const unpaidAmt = unpaid.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);

    return {
      totalMonthly,
      activeCount: active.length,
      annualCommit,
      unpaidCount: unpaid.length,
      unpaidAmt,
      invoiceCount: invoices.length,
    };
  }, [subscriptions, invoices]);

  const n = subscriptions.length;
  const subsWord = n === 1 ? 'subscription' : 'subscriptions';
  const spendSub = fyScoped ? `${n} ${subsWord} in FY` : `${n} ${subsWord} (all-time)`;
  const activeSub = fyScoped
    ? `of ${n} in FY`
    : `of ${n} ${subsWord}`;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
      <KpiCard
        label="Total Monthly Spend"
        value={fmtCurrency(kpis.totalMonthly)}
        sub={spendSub}
        color="bg-blue-500"
      />
      <KpiCard
        label="Active Subscriptions"
        value={String(kpis.activeCount)}
        sub={activeSub}
        color="bg-emerald-500"
      />
      <KpiCard
        label="Annual Commitment"
        value={fmtCurrency(kpis.annualCommit)}
        color="bg-violet-500"
      />
      <KpiCard
        label="Unpaid Invoices"
        value={String(kpis.unpaidCount)}
        sub={
          kpis.unpaidCount > 0 && kpis.unpaidAmt > 0
            ? fmtCurrency(kpis.unpaidAmt)
            : kpis.invoiceCount === 0
              ? 'No invoice records yet'
              : kpis.unpaidCount === 0
                ? 'None outstanding'
                : undefined
        }
        color="bg-amber-500"
      />
    </div>
  );
};
